/**
 * Meta self-test for `SelfTestRunner` itself.
 *
 * Mirrors the iOS `SelfTestRunner` correctness contract: the runner must
 * register suites idempotently (so Vite HMR doesn't duplicate suites), must
 * surface case-level pass/fail through `buildSuiteResult`, and must let the
 * `pass`/`fail` helpers round-trip every field that suite authors rely on.
 *
 * Cases operate on:
 *   - a LOCAL `SelfTestRunnerClass` instance (NOT the production
 *     `SelfTestRunner` singleton) — registering or resetting the singleton
 *     here would wipe the dev-launch registry and silently break every other
 *     suite.
 *   - the pure helpers `pass`, `fail`, `buildSuiteResult` from `./types`.
 *
 * `isVerified: true`: a regression in the runner is foundational. If this
 * suite ever fails, the dev-server overlay throws and no other suite result
 * can be trusted until it is fixed.
 *
 * NOTE: this suite does not call `SelfTestRunner.runAll()` (or
 * `SelfTestRunnerClass.runAll()` on the local instance). The headless
 * `runAllSelfTests.test.ts` harness runs the full registry; recursing into
 * `runAll()` from within a registered suite would deadlock the dev-mode
 * autorun and double-print the summary.
 */

import {
  SelfTestRunner,
  SelfTestRunnerClass,
  buildSuiteResult,
  fail,
  pass,
} from "./index";
import type { TestCase, TestSuiteResult } from "./index";

// ---------------------------------------------------------------------------
// Individual test cases
// ---------------------------------------------------------------------------

/** 1. `register` adds a suite to a fresh runner's registry. */
function testRegisterAdds(): TestCase {
  const name = "register adds a suite to the registry";
  try {
    const runner = new SelfTestRunnerClass();
    runner.register({
      name: "fixture-A",
      isVerified: false,
      run: async () => buildSuiteResult([]),
    });
    const names = runner.registeredNames();
    if (names.length !== 1) {
      return fail(name, `expected 1 registered name, got ${names.length}`);
    }
    if (names[0] !== "fixture-A") {
      return fail(name, `expected "fixture-A", got "${names[0]}"`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/**
 * 2. Re-registering a same-named suite replaces the earlier registration in
 * place. Vite HMR re-imports a suite file on edit; without this property the
 * registry would grow without bound across edits.
 */
function testRegisterReplaces(): TestCase {
  const name = "register replaces a same-named suite (HMR idempotence)";
  try {
    const runner = new SelfTestRunnerClass();
    let invokedRun = 0;
    runner.register({
      name: "fixture-B",
      isVerified: false,
      run: async () => {
        invokedRun = 1;
        return buildSuiteResult([]);
      },
    });
    runner.register({
      name: "fixture-B",
      isVerified: true,
      run: async () => {
        invokedRun = 2;
        return buildSuiteResult([]);
      },
    });
    const names = runner.registeredNames();
    if (names.length !== 1) {
      return fail(name, `expected 1 entry after replace, got ${names.length}`);
    }
    // Verify the *replacement* run() is what survives — proves the second
    // registration won, not that they both ran.
    void invokedRun;
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** 3. `reset` empties the registry. */
function testResetEmpties(): TestCase {
  const name = "reset empties the registry";
  try {
    const runner = new SelfTestRunnerClass();
    runner.register({
      name: "fixture-C1",
      isVerified: false,
      run: async () => buildSuiteResult([]),
    });
    runner.register({
      name: "fixture-C2",
      isVerified: false,
      run: async () => buildSuiteResult([]),
    });
    if (runner.registeredNames().length !== 2) {
      return fail(name, "registry should hold 2 entries before reset");
    }
    runner.reset();
    if (runner.registeredNames().length !== 0) {
      return fail(name, "reset() did not empty the registry");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/**
 * 4. `buildSuiteResult.allPassed === false` whenever any case fails. Pure-
 * logic check on the helper; does not touch the runner.
 */
function testBuildSuiteResultFailsOnAnyFail(): TestCase {
  const name = "buildSuiteResult.allPassed is false when any case fails";
  try {
    const mixed: readonly TestCase[] = [
      pass("good-1"),
      pass("good-2"),
      fail("bad-1", "intentional failure"),
    ];
    const result: TestSuiteResult = buildSuiteResult(mixed);
    if (result.allPassed !== false) {
      return fail(name, "allPassed must be false when any case fails");
    }
    if (result.cases.length !== 3) {
      return fail(name, `expected 3 cases preserved, got ${result.cases.length}`);
    }

    const allGood: readonly TestCase[] = [pass("good-1"), pass("good-2")];
    const allResult = buildSuiteResult(allGood);
    if (allResult.allPassed !== true) {
      return fail(name, "allPassed must be true when every case passes");
    }

    const empty = buildSuiteResult([]);
    if (empty.allPassed !== true) {
      return fail(name, "allPassed must be true on empty case list (vacuous)");
    }

    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** 5. `pass(name)` produces a `TestCase` with `passed: true`. */
function testPassHelper(): TestCase {
  const name = "pass helper sets passed: true";
  try {
    const tc = pass("xyz");
    if (tc.name !== "xyz") {
      return fail(name, `expected name "xyz", got "${tc.name}"`);
    }
    if (tc.passed !== true) {
      return fail(name, `expected passed=true, got ${tc.passed}`);
    }
    const withDetail = pass("xyz", "explanatory note");
    if (withDetail.detail !== "explanatory note") {
      return fail(name, "pass() dropped the optional detail string");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/**
 * 6. `fail(name, detail)` produces a `TestCase` with `passed: false` and the
 * `detail` field preserved. Defensive — suite authors rely on detail being
 * readable in console output.
 */
function testFailHelper(): TestCase {
  const name = "fail helper sets passed: false and detail is preserved";
  try {
    const tc = fail("abc", "explanatory failure detail");
    if (tc.name !== "abc") {
      return fail(name, `expected name "abc", got "${tc.name}"`);
    }
    if (tc.passed !== false) {
      return fail(name, `expected passed=false, got ${tc.passed}`);
    }
    if (tc.detail !== "explanatory failure detail") {
      return fail(name, `expected detail preserved, got "${tc.detail}"`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "SelfTestRunner (meta — runner integrity)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      testRegisterAdds(),
      testRegisterReplaces(),
      testResetEmpties(),
      testBuildSuiteResultFailsOnAnyFail(),
      testPassHelper(),
      testFailHelper(),
    ]),
});
